import React from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import Icon from '../Icon';
import { useAuth } from '../../contexts/AuthContext';
import { usePagePermissions, ROLE_LABEL } from '../../hooks/usePagePermissions';

// Each entry maps to a `dashboard_page` enum value via the `page` field.
// Sidebar renders only the entries the current user has access to.
//
// Items with `superAdminOnly: true` aren't in the page-permission enum
// (yet) — they fall back to the legacy "admin sees it" gate so the
// existing Integrations + Vendors visibility on main is preserved.
const NAV_GROUPS = [
  {
    label: 'Monitoring',
    items: [
      { to: '/',                  page: 'overview',      icon: 'dashboard', label: 'Overview' },
      { to: '/transactions',      page: 'transactions',  icon: 'clock',     label: 'Transactions' },
      { to: '/wallets',           page: 'wallets',       icon: 'wallet',    label: 'Wallets' },
      { to: '/treasury',          page: 'treasury',      icon: 'shield',    label: 'Treasury' },
      { to: '/compliance',        page: 'compliance',    icon: 'shield',    label: 'Compliance' },
      { to: '/agent-reports',     page: 'agent_reports', icon: 'bell',      label: 'Agent Reports' },
      { to: '/integrations',      icon: 'settings',      label: 'Integrations' },
    ],
  },
  {
    label: 'Workflows',
    items: [
      // Careers sits ABOVE Support per product spec. Super-admin sees it
      // by default; other roles need an explicit user_page_permissions grant.
      { to: '/careers',           page: 'careers',       icon: 'user',      label: 'Careers' },
      { to: '/support',           page: 'support',       icon: 'inbox',     label: 'Support' },
      { to: '/documents',         page: 'documents',     icon: 'folder',    label: 'Documents' },
      { to: '/pitch-access',      page: 'pitch_access',  icon: 'shield',    label: 'Pitch Access' },
      { to: '/waitlist',          page: 'waitlist',      icon: 'user',      label: 'Waitlist' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/vendors',           superAdminOnly: true,  icon: 'shield',    label: 'Vendors' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/settings',          page: 'settings',      icon: 'settings',  label: 'Settings' },
    ],
  },
];

const Sidebar = ({ open, onClose }) => {
  const { profile } = useAuth();
  const { hasPage, isSuperAdmin, role } = usePagePermissions();
  const initial = (profile?.email || 'U').slice(0, 1).toUpperCase();

  const isVisible = (item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (!item.page) return true; // un-gated routes (Integrations etc.) shown to all approved users
    return hasPage(item.page);
  };

  return (
    <>
      {/* Mobile-only scrim — taps outside the drawer dismiss it. Only
          rendered when open so it doesn't intercept clicks on desktop. */}
      {open && (
        <div
          className="sidebar-scrim"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside className={clsx('app-sidebar', open && 'open')}>
        <div className="sidebar">
          <div className="sidebar-brand">
            <div className="sidebar-brand-mark">C</div>
            <div className="sidebar-brand-text">
              Coynitt
              <small>Internal Console</small>
            </div>
          </div>

          <div className="sidebar-scroll">
            {NAV_GROUPS.map((group) => {
              const visibleItems = group.items.filter(isVisible);
              if (visibleItems.length === 0) return null;
              return (
                <React.Fragment key={group.label}>
                  <div className="sidebar-section">{group.label}</div>
                  <nav className="sidebar-nav">
                    {visibleItems.map(({ to, icon, label }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === '/'}
                        onClick={onClose}
                        className={({ isActive }) => clsx('nav-item', isActive && 'active')}
                      >
                        <span className="nav-icon"><Icon name={icon} size={20} /></span>
                        {label}
                      </NavLink>
                    ))}
                  </nav>
                </React.Fragment>
              );
            })}
          </div>

          <div className="sidebar-footer">
            <div className="row row-gap-3" style={{ padding: '8px 4px' }}>
              <div className="avatar">{initial}</div>
              <div className="col" style={{ minWidth: 0, flex: 1 }}>
                <div className="font-semibold" style={{ color: 'var(--sb-active)', fontSize: 13 }}>
                  {profile?.full_name || profile?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-xs" style={{ color: 'var(--sb-text)' }}>
                  {ROLE_LABEL[role] || 'Member'}{profile?.status === 'pending' ? ' · pending' : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
